import { jsxs, jsx } from "react/jsx-runtime";
import React__default, { useState, useRef, useEffect, useCallback } from "react";
import { isTouchDevice } from "@perxis-auto/shared";
import { useJsApiLoader, GoogleMap, OverlayView } from "@react-google-maps/api";
import FullscreenControl from "../../controls/FullscreenControl/FullscreenControl.js";
import ZoomControl from "../../controls/ZoomControl/ZoomControl.js";
import { MAP_CENTER } from "../../Map.constants.js";
import { getMapStyles } from "../../Map.utils.js";
import Balloon from "./Balloon.js";
import { SINGLE_PIN_ZOOM, DEFAULT_PLACEMARK_SIZE, DEFAULT_CLUSTER_SIZE, MIN_ZOOM, MAP_CONTAINER_STYLE, DEFAULT_PLACEMARK_OFFSET } from "./constants.js";
import GeoObjects from "./GeoObjects.js";
import Hint from "./Hint.js";
import { normalizeOccupiedArea, calculatePadding } from "./utils.js";
const isMobile = isTouchDevice();
const Google = ({
  // placemark
  placemarkImage,
  placemarkActiveImage,
  placemarkHoverImage,
  placemarkOffset = DEFAULT_PLACEMARK_OFFSET,
  placemarkHoverOffset = placemarkOffset,
  placemarkActiveOffset = placemarkOffset,
  placemarkSize = DEFAULT_PLACEMARK_SIZE,
  placemarkHoverSize = placemarkSize,
  placemarkActiveSize = placemarkSize,
  // cluster
  enableClustering = false,
  clusterImage,
  clusterHoverImage,
  clusterSize = DEFAULT_CLUSTER_SIZE,
  clusterHoverSize = clusterSize,
  // zoom
  minZoom = MIN_ZOOM,
  maxZoom,
  zoomControl = !isMobile,
  shouldZoomOnClickPin = false,
  // fullscreen
  fullscreenControl = isMobile,
  onPressFullscreen,
  // balloon
  enableBalloon = false,
  balloonContent,
  // hint
  enableHint = false,
  hintContent,
  mapsKey,
  selected,
  hovered,
  pins,
  language = "ru",
  region = "RU",
  styles,
  multiTouch,
  scrollZoom,
  occupiedAreas,
  // handlers
  onSelectPin,
  onHoverPin,
  onMapClick
}) => {
  const [map, setMap] = useState();
  const [projection, setProjection] = useState(null);
  const mapStyles = useRef();
  const { isLoaded } = useJsApiLoader({ id: "google-map-script", googleMapsApiKey: mapsKey, language, region });
  useEffect(() => {
    if (!map) {
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    if (!pins.length) {
      return;
    }
    pins.forEach((pin) => {
      bounds.extend({ lat: pin.latitude, lng: pin.longitude });
    });
    const mapDiv = map.getDiv();
    const { width, height } = mapDiv.getBoundingClientRect();
    const mapContainerSize = { width, height };
    const normalizedAreas = (occupiedAreas == null ? void 0 : occupiedAreas.map((area) => normalizeOccupiedArea(area, mapContainerSize))) || [];
    const padding = calculatePadding(normalizedAreas, mapContainerSize);
    if (pins.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(SINGLE_PIN_ZOOM);
      map.setOptions({ maxZoom: SINGLE_PIN_ZOOM });
      map.fitBounds(bounds, padding);
      map.setOptions({ maxZoom });
      return;
    }
    map.fitBounds(bounds, padding);
  }, [pins, map, occupiedAreas, placemarkSize, clusterSize, maxZoom, projection]);
  useEffect(() => {
    getMapStyles(styles).then((res) => {
      mapStyles.current = res;
    });
  }, [styles]);
  const handleOverlayLoad = useCallback((overlay) => {
    const proj = overlay.getProjection();
    if (proj) {
      setProjection(proj);
    }
  }, []);
  const onLoad = useCallback((map2) => {
    setMap(map2);
  }, []);
  const onUnmount = useCallback((map2) => {
    setMap(void 0);
  }, []);
  const onClick = useCallback(
    (e) => {
      var _a, _b;
      if (!onMapClick) {
        return;
      }
      onMapClick({
        lat: ((_a = e.latLng) == null ? void 0 : _a.lat()) ?? 0,
        lng: ((_b = e.latLng) == null ? void 0 : _b.lng()) ?? 0,
        pageX: e.domEvent.screenX,
        pageY: e.domEvent.screenY
      });
    },
    [onMapClick]
  );
  if (!isLoaded) {
    return null;
  }
  return /* @__PURE__ */ jsxs(
    GoogleMap,
    {
      mapContainerStyle: MAP_CONTAINER_STYLE,
      center: MAP_CENTER,
      onLoad,
      onUnmount,
      onClick,
      options: {
        minZoom,
        maxZoom,
        disableDefaultUI: true,
        gestureHandling: multiTouch ? "greedy" : "none",
        scrollwheel: scrollZoom,
        clickableIcons: false,
        styles: mapStyles.current
      },
      children: [
        /* @__PURE__ */ jsx(
          GeoObjects,
          {
            pins,
            selected,
            hovered,
            enableClustering,
            clusterImage,
            clusterHoverImage,
            clusterSize,
            clusterHoverSize,
            placemarkImage,
            placemarkHoverImage,
            placemarkActiveImage,
            placemarkOffset,
            placemarkHoverOffset,
            placemarkActiveOffset,
            placemarkSize,
            placemarkHoverSize,
            placemarkActiveSize,
            shouldZoomOnClickPin,
            onSelectPin,
            onHoverPin
          }
        ),
        /* @__PURE__ */ jsx(Balloon, { pins, selected, enableBalloon, balloonContent }),
        /* @__PURE__ */ jsx(Hint, { pins, hovered, enableHint, hintContent }),
        zoomControl && /* @__PURE__ */ jsx(
          ZoomControl,
          {
            onZoom: () => map == null ? void 0 : map.setZoom(((map == null ? void 0 : map.getZoom()) ?? 0) + 1),
            onUnZoom: () => map == null ? void 0 : map.setZoom(((map == null ? void 0 : map.getZoom()) ?? 0) - 1)
          }
        ),
        fullscreenControl && /* @__PURE__ */ jsx(FullscreenControl, { occupiedAreas, onPress: onPressFullscreen }),
        /* @__PURE__ */ jsx(OverlayView, { mapPaneName: OverlayView.OVERLAY_MOUSE_TARGET, position: MAP_CENTER, onLoad: handleOverlayLoad, children: /* @__PURE__ */ jsx("div", {}) })
      ]
    }
  );
};
const Google$1 = React__default.memo(Google);
export {
  Google$1 as default
};
//# sourceMappingURL=Google.js.map
